// src/app/solution/[id]/page.js
'use client';
import React from 'react';
import { useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import styles from './page.module.css';
import SolutionProducts from "@/solution/[id]/SolutionProducts";
import { useSolution } from './solution-context';
import StructuredData from "@/Components/StructuredData/StructuredData";
import { buildBreadcrumbSchema, buildCollectionPageSchema } from "@/lib/structured-data";
import {
    getWebsiteProductImageUrl,
    getWebsiteProductSummaryHtml,
} from "@/lib/product-presentation";

const KIT_FEATURE_META = {
    coretest: { label: "Coretest", title: "Coretest" },
    healthcheck: { label: "Healthcheck", title: "Healthcheck" },
    interactive: { label: "Interactive", title: "Interactive Tests" },
    uav: { label: "UAV", title: "UAV Rescue" },
    qtp: { label: "QTP", title: "QuickTech Premium Duo" },
    pco: { label: "PCO", title: "PCOptimizer (PCO)" },
    qtia: { label: "QTIA", title: "QuickTech Self-Boot for Apple" },
    da: { label: "DA", title: "Drive Analyzer" },
    dr: { label: "DR", title: "Data Recovery (DR)" },
    dt: { label: "DT", title: "DataTransfer (DT)" },
    wstpusb: { label: "WSTPUSB", title: "WinStress Pro" },
    de: { label: "DE", title: "Drive Erase (DE)" },
    ultrapost: { label: "ULTRAPOST", title: "UltraP.O.S.T." },
    qcs: { label: "QCS", title: "QuickCast DDR Server" },
    pci: { label: "PCI", title: "PC Inspector" },
};

const FALLBACK_KIT_PRODUCTS = {
    elemental: ["coretest", "healthcheck", "interactive"],
    essential: ["coretest", "healthcheck", "interactive", "qtp", "qtia", "uav"],
    ultra: [
        "coretest",
        "healthcheck",
        "interactive",
        "qtp",
        "pco",
        "qtia",
        "da",
        "dr",
        "dt",
        "wstpusb",
        "uav",
        "de",
        "ultrapost",
        "qcs",
        "pci",
    ],
};

function normalizeSlug(value) {
    return String(value || "").trim().toLowerCase();
}

function getKitProductSlugs(kit) {
    const liveSlugs = Array.isArray(kit.related_products)
        ? kit.related_products.map((product) => normalizeSlug(product?.alt_id)).filter(Boolean)
        : [];

    return liveSlugs.length ? liveSlugs : FALLBACK_KIT_PRODUCTS[normalizeSlug(kit.alt_id)] || [];
}

function KitProducts({ kits = [] }) {
    const featureSlugs = Array.from(new Set(kits.flatMap(getKitProductSlugs)));

    return (
        <div className={styles.kitGrid}>
            {kits.map((kit) => {
                const slug = normalizeSlug(kit.alt_id);
                const includedSlugs = getKitProductSlugs(kit);
                const relatedProducts = Array.isArray(kit.related_products) ? kit.related_products : [];
                const relatedBySlug = new Map(
                    relatedProducts
                        .map((product) => [normalizeSlug(product?.alt_id), product])
                        .filter(([productSlug]) => Boolean(productSlug))
                );
                const included = new Set(includedSlugs);
                const image = getWebsiteProductImageUrl(kit);
                const summary = getWebsiteProductSummaryHtml(kit);

                return (
                    <article key={kit.id} className={`${styles.kitCard} ${styles[`kitCard${slug}`] || ""}`}>
                        <div
                            className={styles.kitCardHeader}
                            style={{ backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.12), rgba(0, 0, 0, 0.12)), url(${image})` }}
                        >
                            <h3 className={styles.kitHeading}>{kit.name}</h3>
                            <div
                                className={styles.kitDescription}
                                dangerouslySetInnerHTML={{ __html: summary }}
                            />
                        </div>
                        <div className={styles.kitCardBody}>
                            <div className={styles.kitDivider} />
                            <div className={styles.kitFeatureGrid}>
                                {featureSlugs.map((featureSlug) => {
                                    const relatedProduct = relatedBySlug.get(featureSlug);
                                    const fallbackFeature = KIT_FEATURE_META[featureSlug] || {};
                                    const feature = {
                                        label: fallbackFeature.label || featureSlug.toUpperCase(),
                                        title: relatedProduct?.name || fallbackFeature.title || featureSlug,
                                    };
                                    const isIncluded = included.has(featureSlug);
                                    return (
                                        <span
                                            key={`${slug}-${featureSlug}`}
                                            className={`${styles.kitFeature} ${isIncluded ? styles.kitFeatureIncluded : styles.kitFeatureMuted}`}
                                            data-title={feature.title}
                                            data-status={isIncluded ? "Included in this Kit" : "Not included in this Kit"}
                                            data-active={isIncluded ? "true" : "false"}
                                            tabIndex={0}
                                        >
                                            {feature.label}
                                        </span>
                                    );
                                })}
                            </div>
                            <div className={styles.kitDivider} />
                        </div>
                        <Link href={`/kit/${kit.alt_id}`} className={styles.kitDetailsButton}>
                            More Details
                        </Link>
                    </article>
                );
            })}
        </div>
    );
}

export default function SolutionPage() {
    const { id, meta, products, kits: sharedKits = [] } = useSolution();
    const isKitProduct = (product) => {
        const kind = String(product?.kind || "").trim().toUpperCase();
        return kind === "KIT" || kind === "BUNDLE";
    };
    const kits = sharedKits.length ? sharedKits : products.filter(isKitProduct);
    const solutionProducts = products.filter((product) => !isKitProduct(product));

    useEffect(() => {
        const hash = window.location.hash?.replace('#', '');
        if (!hash) {
            return;
        }

        const target = document.getElementById(hash);
        if (!target) {
            return;
        }

        requestAnimationFrame(() => {
            target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
    }, [id, products.length]);

    if (!products.length) {
        return <p className={styles.error}>No products found for id.</p>;
    }

    const pageUrl = `https://uxd.com/solution/${id}`;

    return (
        <div id="solution-content" className={`ux-container ${styles.wrapper} ${styles.contentAnchor}`}>
            <StructuredData
                data={[
                    buildBreadcrumbSchema([
                        { name: "Home", path: "/" },
                        { name: meta.title, path: `/solution/${id}` },
                    ]),
                    buildCollectionPageSchema({
                        title: meta.title,
                        description: meta.description,
                        path: `/solution/${id}`,
                        products,
                    }),
                ]}
            />
            {kits.length > 0 && (
                <section className={styles.kitsSection} aria-labelledby="solution-kits-title">
                    <div className={styles.sectionHeader}>
                    </div>
                    <KitProducts kits={kits} />
                </section>
            )}

            <div className={styles.header}>
                <div className={styles.headerImage}>
                    <Image
                        src={`/assets/images/solutions/${id}.png`}
                        alt={meta.title}
                        fill
                        objectFit="cover"
                        priority
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    />
                </div>

                <div className={styles.headerDesc}>
                    <h1 className={styles.title}>{meta.title}</h1>
                    <div
                        className={styles.descHtml}
                        dangerouslySetInnerHTML={{ __html: meta.description }}
                    />
                    <div className={styles.share}>
                        <span>Share:</span>
                        <a
                            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`}
                            target="_blank"
                            rel="noreferrer"
                        >
                            <i className="fab fa-facebook-f" />
                        </a>
                        <a
                            href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(pageUrl)}`}
                            target="_blank"
                            rel="noreferrer"
                        >
                            <i className="fab fa-linkedin-in" />
                        </a>
                        <a
                            href={`https://pinterest.com/pin/create/button/?url=${encodeURIComponent(pageUrl)}`}
                            target="_blank"
                            rel="noreferrer"
                        >
                            <i className="fab fa-pinterest" />
                        </a>
                        <a
                            href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}`}
                            target="_blank"
                            rel="noreferrer"
                        >
                            <i className="fab fa-twitter" />
                        </a>
                    </div>
                </div>

                <div className={styles.headerBenefits}>
                    <h2 className={styles.benefitsTitle}>Key Benefits</h2>
                    <div
                        className={styles.benefitsContent}
                        dangerouslySetInnerHTML={{ __html: meta.benefits }}
                    />
                </div>
            </div>

            <section id="solution-products" className={styles.productsSection}>
                <SolutionProducts products={solutionProducts} />
            </section>
        </div>
    );
}
